import { useEffect, useRef, useState } from "react";

type RichContentEditorProps = {
  value: string;
  onChange: (value: string) => void;
  minHeight?: number;
};

type FormatButton = {
  label: string;
  icon: string;
  open: string;
  close: string;
  placeholder: string;
};

const formatButtons: FormatButton[] = [
  { label: "Negrito", icon: "ri-bold", open: "<strong>", close: "</strong>", placeholder: "texto em negrito" },
  { label: "Italico", icon: "ri-italic", open: "<em>", close: "</em>", placeholder: "texto em italico" },
  { label: "Sublinhado", icon: "ri-underline", open: "<u>", close: "</u>", placeholder: "texto sublinhado" },
  { label: "Titulo", icon: "ri-heading", open: "<h2>", close: "</h2>", placeholder: "Titulo da secao" },
  { label: "Paragrafo", icon: "ri-text", open: "<p>", close: "</p>", placeholder: "Texto do paragrafo" },
  { label: "Lista", icon: "ri-list-unordered", open: "<ul>\n  <li>", close: "</li>\n</ul>", placeholder: "Item da lista" },
  { label: "Lista numerada", icon: "ri-list-ordered", open: "<ol>\n  <li>", close: "</li>\n</ol>", placeholder: "Item da lista" },
];

const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;

function htmlToPlainText(value: string) {
  if (!value) return "";

  return value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li>/gi, "- ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function plainTextToHtml(value: string) {
  return value
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, "<br/>")}</p>`)
    .join("\n");
}

function initialTextValue(value: string) {
  return htmlTagPattern.test(value) ? htmlToPlainText(value) : value;
}

export default function RichContentEditor({
  value,
  onChange,
  minHeight = 300,
}: RichContentEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lastEmittedValueRef = useRef(value || "");
  const [draft, setDraft] = useState(initialTextValue(value || ""));
  const [mode, setMode] = useState<"text" | "html" | "preview">("text");

  useEffect(() => {
    if ((value || "") === lastEmittedValueRef.current) return;
    setDraft(mode === "text" ? initialTextValue(value || "") : value || "");
  }, [value, mode]);

  const updateDraft = (nextValue: string) => {
    const nextOutputValue = mode === "text" ? plainTextToHtml(nextValue) : nextValue;

    setDraft(nextValue);
    lastEmittedValueRef.current = nextOutputValue;
    onChange(nextOutputValue);
  };

  const switchMode = (nextMode: "text" | "html" | "preview") => {
    if (nextMode === mode) return;

    if (nextMode === "text") {
      setDraft(htmlToPlainText(mode === "html" ? draft : value));
    } else if (mode === "text") {
      setDraft(plainTextToHtml(draft));
    } else {
      setDraft(value || draft);
    }

    setMode(nextMode);
  };

  const insertAroundSelection = (open: string, close: string, placeholder: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = draft.slice(start, end) || placeholder;
    const nextValue = `${draft.slice(0, start)}${open}${selectedText}${close}${draft.slice(end)}`;
    const cursorStart = start + open.length;
    const cursorEnd = cursorStart + selectedText.length;

    updateDraft(nextValue);

    window.requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const addLink = () => {
    const url = window.prompt("Cole a URL do link:");
    if (!url) return;
    insertAroundSelection(
      `<a href="${url}" target="_blank" rel="noopener noreferrer">`,
      "</a>",
      "texto do link",
    );
  };

  return (
    <div className="border border-alvinegro-border rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-alvinegro-border bg-alvinegro-light/60 px-2 py-2">
        {mode === "html" && (
          <>
            {formatButtons.map((button) => (
              <button
                key={button.label}
                type="button"
                onClick={() => insertAroundSelection(button.open, button.close, button.placeholder)}
                title={button.label}
                className="w-8 h-8 rounded-md flex items-center justify-center text-alvinegro-dark hover:bg-white transition-colors"
              >
                <i className={button.icon} />
              </button>
            ))}

            <button
              type="button"
              onClick={addLink}
              title="Hiperlink"
              className="w-8 h-8 rounded-md flex items-center justify-center text-alvinegro-dark hover:bg-white transition-colors"
            >
              <i className="ri-link" />
            </button>
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => switchMode("text")}
            className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${mode === "text" ? "bg-alvinegro-black text-white" : "bg-white text-alvinegro-dark hover:bg-alvinegro-border"}`}
          >
            Texto
          </button>
          <button
            type="button"
            onClick={() => switchMode("html")}
            className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${mode === "html" ? "bg-alvinegro-black text-white" : "bg-white text-alvinegro-dark hover:bg-alvinegro-border"}`}
          >
            HTML
          </button>
          <button
            type="button"
            onClick={() => switchMode("preview")}
            className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${mode === "preview" ? "bg-alvinegro-black text-white" : "bg-white text-alvinegro-dark hover:bg-alvinegro-border"}`}
          >
            Previa
          </button>
        </div>
      </div>

      {mode === "preview" ? (
        <div
          className="rich-content-preview w-full p-4 text-sm text-alvinegro-dark leading-relaxed"
          style={{ minHeight }}
          dangerouslySetInnerHTML={{ __html: htmlTagPattern.test(draft) ? draft : plainTextToHtml(draft) }}
        />
      ) : (
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(event) => updateDraft(event.target.value)}
          className={`w-full p-4 text-sm text-alvinegro-dark focus:outline-none resize-y ${mode === "html" ? "font-mono" : ""}`}
          style={{ minHeight }}
          spellCheck={mode === "text"}
          placeholder={mode === "html" ? "<p>Escreva ou cole o HTML da noticia aqui...</p>" : "Escreva a noticia em texto normal..."}
        />
      )}
    </div>
  );
}
